import { useEffect, useRef, useState } from 'react';

/**
 * PromptSheet — the white, square-cornered sheet the install and notifications
 * prompts share (design_handoff_install_notify_icons, Oct 2026). A scrim sits
 * behind it; tapping the scrim dismisses. `direction="up"` slides in from the
 * bottom (install), `"down"` drops from the top (notifications).
 *
 * The sheet stays mounted through its exit animation: callers hold `open`, flip
 * it false to close, and get `onClosed` once the exit has run so they can
 * unmount. usePromptClose below wraps that bookkeeping.
 */
const MONO = "'JetBrains Mono', ui-monospace, monospace";
const HUMAN = 'Arial, Helvetica, sans-serif';
export const APP_ICON = '/portal-app-icon-512.png';

export const EXIT_MS = { up: 240, down: 260, card: 280 };

/** show → closing → gone. `close()` runs the exit animation, then `onGone`. */
export function usePromptClose(exitMs, onGone) {
  const [closing, setClosing] = useState(false);
  const timer = useRef(null);
  const goneRef = useRef(onGone);
  goneRef.current = onGone;
  useEffect(() => () => clearTimeout(timer.current), []);
  const close = () => {
    if (timer.current) return;
    setClosing(true);
    timer.current = setTimeout(() => { timer.current = null; setClosing(false); goneRef.current?.(); }, exitMs);
  };
  return [closing, close];
}

export function CloseButton({ onClick, size = 36, style }) {
  const glyph = Math.round(size * 14 / 36);
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Close"
      className="pr-prompt-btn pr-prompt-invert"
      style={{
        position: 'absolute', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: 'none', background: '#F2F2F2', borderRadius: '50%', color: '#000', cursor: 'pointer', padding: 0, ...style,
      }}
    >
      <svg width={glyph} height={glyph} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square">
        <path d="M5 5l14 14M19 5L5 19" />
      </svg>
    </button>
  );
}

export function DragHandle({ style }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', ...style }}>
      <span style={{ width: 40, height: 4, borderRadius: 2, background: '#D4D4D4' }} />
    </div>
  );
}

/** The 96px app icon; `badge` renders over its bottom-right corner. */
export function AppIconTile({ badge }) {
  return (
    <span style={{ position: 'relative', width: 96, height: 96, flexShrink: 0 }}>
      <img
        src={APP_ICON}
        alt=""
        style={{ width: 96, height: 96, borderRadius: 22, display: 'block', boxShadow: '0 0 0 1px rgba(0,0,0,.08)' }}
      />
      {badge}
    </span>
  );
}

export function PromptCopy({ eyebrow, title, body }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.22em', color: '#6B7280' }}>{eyebrow}</span>
      <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: '.06em', lineHeight: 1.2, textWrap: 'balance' }}>{title}</h2>
      <p style={{ margin: 0, fontFamily: HUMAN, fontSize: 15, lineHeight: 1.5, color: '#333', textWrap: 'pretty' }}>{body}</p>
    </div>
  );
}

export function PromptActions({ primaryLabel, onPrimary, primaryDisabled, secondaryLabel = 'NOT NOW', onSecondary }) {
  return (
    <div style={{ padding: '24px 24px 34px', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <button
        type="button"
        onClick={onPrimary}
        disabled={primaryDisabled}
        className="pr-prompt-btn pr-prompt-invert-dark"
        style={{ height: 56, border: '2px solid #000', background: '#000', color: '#fff', font: 'inherit', fontSize: 13, fontWeight: 700, letterSpacing: '.18em', cursor: 'pointer' }}
      >
        {primaryLabel}
      </button>
      <button
        type="button"
        onClick={onSecondary}
        className="pr-prompt-btn pr-prompt-invert"
        style={{ height: 48, border: 'none', background: '#fff', color: '#000', font: 'inherit', fontSize: 12, fontWeight: 700, letterSpacing: '.18em', cursor: 'pointer' }}
      >
        {secondaryLabel}
      </button>
    </div>
  );
}

export default function PromptSheet({ direction = 'up', closing, onDismiss, label, children }) {
  const fromTop = direction === 'down';
  const anim = fromTop ? (closing ? 'pr-drop-out' : 'pr-drop-in') : (closing ? 'pr-sheet-down' : 'pr-sheet-up');
  return (
    <div className="fixed inset-0 z-50" style={{ fontFamily: MONO, color: '#000' }}>
      {!closing && <div className="pr-scrim-in" onClick={onDismiss} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.55)' }} />}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className={`${anim} sm:max-w-[480px] sm:mx-auto`}
        style={{
          position: 'absolute', left: 0, right: 0,
          top: fromTop ? 0 : 'auto', bottom: fromTop ? 'auto' : 0,
          paddingTop: fromTop ? 'calc(44px + env(safe-area-inset-top, 0px))' : 0,
          paddingBottom: fromTop ? 0 : 'env(safe-area-inset-bottom, 0px)',
          background: '#fff', display: 'flex', flexDirection: 'column',
        }}
      >
        {children}
      </div>
    </div>
  );
}
